import * as React from 'react';

import cx from 'classnames';
import flattenChildren from 'react-keyed-flatten-children';

import { Box } from '~shared/components/Box';
import { Divider } from '~shared/components/Divider';
import {
  AlignX,
  alignDirectionToCol,
  useFlexAlign,
} from '~shared/hooks/useFlexAlign';
import { ReactNodeNoStrings } from '~shared/types/ReactNodeNoStrings';
import warning from '~shared/utils/warning';

const validStackElements = ['div', 'ol', 'ul'] as const;
const spaceClassNames = {
  '2': {
    space: 'pt-2',
    offset: 'before:-mt-2',
  },
  '4': {
    space: 'pt-4',
    offset: 'before:-mt-4',
  },
  '8': {
    space: 'pt-8',
    offset: 'before:-mt-8',
  },
};

export interface StackProps {
  children: ReactNodeNoStrings;
  element?: typeof validStackElements[number];
  space?: 'none' | keyof typeof spaceClassNames;
  dividers?: boolean;
  align?: AlignX;
}

export function Stack({
  children,
  element = 'div',
  space = 'none',
  dividers = false,
  align,
}: StackProps) {
  warning(
    validStackElements.includes(element),
    `Invalid Stack element: '${element}'. Should be one of [${validStackElements
      .map((c) => `'${c}'`)
      .join(', ')}]`
  );

  const stackItems = flattenChildren(children);
  const isList = element === 'ol' || element === 'ul';
  const itemElement = isList ? 'li' : 'div';

  const spaceClass = space !== 'none' ? spaceClassNames[space] : null;
  const parentClass = cx(
    spaceClass && ['before:block before:empty-content', spaceClass.offset],
    isList && ['m-0 p-0 list-none']
  );
  // Swap align setting due to `flex-direction` being set to `column`.
  const alignY = alignDirectionToCol(align);
  const { itemsClass } = useFlexAlign({ alignY });
  const itemClass = cx(
    spaceClass?.space,
    align && ['flex flex-col', itemsClass]
  );

  return (
    <Box tagName={element} className={parentClass}>
      {React.Children.map(stackItems, (child, index) => (
        <Box tagName={itemElement} className={itemClass}>
          {dividers && index > 0 ? (
            <>
              <Divider />
              <div className={spaceClass?.space}>{child}</div>
            </>
          ) : (
            child
          )}
        </Box>
      ))}
    </Box>
  );
}
